import { computed, inject, Injectable, signal } from '@angular/core';
import { PROPOSAL_STATUS_DISABLED, PROPOSAL_VERSION_STATUS } from '@shared/components/base-classes/base-group/constants';
import _ from 'lodash';
import { Observable } from 'rxjs';
import { map, pluck } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CommonService } from './common.service';
export const enum STEP_NAME {
  ProposalInformation = 'PROPOSAL_INFORMATION',
  ProductsAndPlans = 'PRODUCTS_AND_PLANS',
  Benefit = 'BENEFIT',
  MemberUpload = 'MEMBER_UPLOAD',
  PremiumSummray = 'PREMIUM_SUMMARY',
  ProposalConfirm = 'PROPOSAL_CONFIRM',
}
@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  private readonly commonService = inject(CommonService);
  private readonly configurationUrl: string = environment.API_GROUPCONFIGURATION_SERVER_URL;
  private observable: Observable<any> | null = null;
  registration = signal<Record<string, any>>({});
  proposal: Record<string, any> = {};
  links: Record<string, { href: string }> = {};
  pic: Record<string, any> = {};
  proposal$ = signal({});
  isEditField = signal(true);
  isCreateProposal = signal(true);
  isCreateVersion = signal(true);
  disableVersion = computed(() => {
    if (!this.isEditField()) {
      return true;
    }
    const versionStatus = this.proposal$()['versionStatus'];
    return [
      PROPOSAL_VERSION_STATUS.PRINTED,
      PROPOSAL_VERSION_STATUS.PENDING_UW_CHECKED,
      PROPOSAL_VERSION_STATUS.PENDING_FOR_UW_CHECKED,
      PROPOSAL_VERSION_STATUS.TRANSFERRED,
      PROPOSAL_VERSION_STATUS.LOCKED,
      PROPOSAL_VERSION_STATUS.RETURNED,
      PROPOSAL_VERSION_STATUS.PENDING_FOR_UW_ADJUSTMENT,
      PROPOSAL_VERSION_STATUS.EXPIRED,
      PROPOSAL_VERSION_STATUS.CONFIRMED,
      PROPOSAL_VERSION_STATUS.DECLINE,
      PROPOSAL_VERSION_STATUS.DECLINED,
      PROPOSAL_STATUS_DISABLED.PENDING_UW_CHECK,
    ].includes(versionStatus);
  });
  disableSaleForceStatement = computed(() => {
    if (!this.isEditField()) {
      return true;
    }
    const versionStatus = this.proposal$()['versionStatus'];
    return [
      PROPOSAL_VERSION_STATUS.PENDING_UW_CHECKED,
      PROPOSAL_VERSION_STATUS.PENDING_FOR_UW_CHECKED,
      PROPOSAL_VERSION_STATUS.LOCKED,
      PROPOSAL_VERSION_STATUS.RETURNED,
      PROPOSAL_VERSION_STATUS.EXPIRED,
      PROPOSAL_VERSION_STATUS.CONFIRMED,
      PROPOSAL_VERSION_STATUS.DECLINE,
      PROPOSAL_VERSION_STATUS.DECLINED,
      PROPOSAL_STATUS_DISABLED.PENDING_UW_CHECK,
    ].includes(versionStatus);
  });
  isConfirmed = computed(() => this.proposal$()['hasUWReqWaitingPeriod'] === 'N');
  getProposal(id: string, refreshData: boolean = false): Observable<any> {
    if (this.observable == null || refreshData) {
      const url = `${environment.API_PROPOSAL_SERVER_URL}/${id}`;
      this.observable = this.commonService.commonHttpObservable(url).pipe(
        pluck('body'),
        map((proposal: any) => {
          this.proposal = proposal;
          this.proposal$.set(proposal);
          this.links = this.proposal['_links'];
          this.commonService.setState('currentProduct', this.proposal);
          return this.proposal;
        })
      );
    }

    return this.observable;
  }

  getPic(id: string, refreshData: boolean = false): Observable<any> {
    if (this.observable == null || refreshData) {
      const url = `${environment.API_PROPOSAL_SERVER_URL}/${id}/pic`;
      this.observable = this.commonService.commonHttpObservable(url).pipe(
        pluck('body'),
        map((pic: any) => {
          this.pic = pic;
          return this.pic;
        })
      );
    }

    return this.observable;
  }

  validateStep(step: STEP_NAME) {
    switch (step) {
      case STEP_NAME.ProductsAndPlans:
        return this.validateProductAndPlan();
      case STEP_NAME.Benefit:
        break;
      case STEP_NAME.MemberUpload:
        return this.validateMemberUpload();
      case STEP_NAME.PremiumSummray:
        return this.isConfirmed();
      default:
        break;
    }
    return true;
  }

  async validateProductAndPlan() {
    try {
      this.commonService.setLoading(true);
      const url = this.links['self'].href;
      let res = await this.commonService.commonGet(`${url}/plans/status_report?include=coverage,rider`);
      this.commonService.setLoading(false);
      if (res.body.document_status == 'Invalid') {
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.error(error);
      this.commonService.setLoading(false);
    }
  }

  async getMemberUploadMode() {
    const url = this.links['self'].href;
    const result = await this.commonService.commonGet(`${url}/indicatives/totalMember`);
    return _.isEmpty(_.get(result, 'body.indicativePlans')) ? 'uploadMember' : 'indicative';
  }

  async validateMemberUpload() {
    this.commonService.setLoading(true);
    const url = this.links['self'].href;
    const mode = await this.getMemberUploadMode();
    let isValid = true;
    let errorMessage = [];
    let validation = {};
    if (mode === 'indicative') {
      validation = await this.commonService.commonGet(`${url}/indicatives/status_report`);
    } else {
      validation = await this.commonService.commonGet(`${url}/member/status_report`);
    }
    isValid = _.get(validation, 'body.document_status', '').toLocaleLowerCase() === 'valid';
    errorMessage = _.get(validation, 'body.message', []);
    this.commonService.setLoading(false);
    if (!isValid) {
      this.commonService.openDialogsInSequence(errorMessage);
      return false;
    }

    return true;
  }

  copyVersion(copyUrl?: string) {
    const url = copyUrl || this.links['quote:copy_version'].href;
    return this.commonService.commonPost(url);
  }
  patchProposal$(id: string, body: any) {
    const url = `${environment.API_PROPOSAL_SERVER_URL}/${id}`;
    return this.commonService.commonHttpObservable(url, { method: 'patch', body }).pipe(map((data) => data.body));
  }
  async getProductArr(contractType) {
    try {
      let url = `${this.configurationUrl}/getProduct/${contractType}`;
      let res = await this.commonService.commonGet(url);
      if (res) {
        const riders = res.body[0].riders;
        const coverages = res.body[0].coverages;
        const newProductArray = [...riders, ...coverages].map((item) => ({
          prodName: item.label,
          prodCode: item.value,
        }));
        return newProductArray;
      }
    } catch (error) {
      console.error(error);
    }
  }

  updateEditFieldFromStorage(): void {
    const groupSalesAct = localStorage.getItem('groupSalesAct');
    if (groupSalesAct) {
      try {
        const parsed = JSON.parse(groupSalesAct);
        const isEdit = parsed?.proposal?.actions?.Edit ?? true;
        const isCreate = parsed?.proposal?.actions?.Add ?? true;
        const isCreateVersion = parsed?.proposal?.actions?.AddVersion ?? true;
        this.isEditField.set(isEdit);
        this.isCreateProposal.set(isCreate);
        this.isCreateVersion.set(isCreateVersion);
      } catch (err) {
        console.error('Failed to parse groupSalesAct:', err);
        this.isEditField.set(true);
        this.isCreateProposal.set(true);
        this.isCreateVersion.set(true);
      }
    }
  }

  getRegistration$(registrationId: string) {
    const url = `${environment.API_REGISTRATION_SERVER_URL}/${registrationId}`;
    return this.commonService.commonHttpObservable(url).pipe(map((data) => data.body));
  }
}
